import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Form, Button, Card, InputGroup, Container, Row, Col } from 'react-bootstrap';
import { BsSearch, BsPerson } from 'react-icons/bs';
import Table from '../components/Table.tsx';
import './Sanctions.css';

interface SanctionItem {
  _id: string;
  Moderator: string;
  reason: string;
  type: string;
  date?: string;
  server?: string;
}

interface CasierData {
  _id: string;
  GuildID: string;
  id: string;
  sanction: SanctionItem[];
}

interface GuildOption {
  id: string;
  name: string;
  isAdmin: boolean;
}

const Sanctions: React.FC = () => {
  const authUrl = process.env.NODE_ENV === 'development'
    ? `${window.location.protocol}//${window.location.hostname}:5000/auth/discord`
    : '/auth/discord';
  const [sanctions, setSanctions] = useState<CasierData[]>([]);
  const [userId, setUserId] = useState('');
  const [guilds, setGuilds] = useState<GuildOption[]>([]);
  const [guildId, setGuildId] = useState('');
  const [reauthRequired, setReauthRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    axios.get<{ guilds: GuildOption[]; reauthRequired?: boolean }>('/api/guilds')
      .then(({ data }) => {
        const availableGuilds = Array.isArray(data.guilds) ? data.guilds : [];
        setGuilds(availableGuilds);
        setReauthRequired(Boolean(data.reauthRequired) || availableGuilds.length === 0);
        if (availableGuilds.length === 1) setGuildId(availableGuilds[0].id);
      })
      .catch(() => setError('Impossible de charger les serveurs autorisés.'));
  }, []);

  const fetchUserSanctions = async (searchUserId: string) => {
    if (!searchUserId || !guildId) {
      setError('Sélectionnez un serveur et saisissez un identifiant utilisateur.');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get<CasierData[]>(`/api/guilds/${guildId}/sanctions/users/${searchUserId}`);
      setSanctions(response.data);
      setError(null);
    } catch (err) {
      setError('Impossible de récupérer les sanctions.');
      setSanctions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (userId: string, sanctionId: string, data: any) => {
    try {
      const response = await axios.put(`/api/guilds/${guildId}/sanctions/users/${userId}/${sanctionId}`, {
        reason: data.reason,
        type: data.type
      });
      
      // Actualise la liste après la modification.
      if (response.data) {
        await fetchUserSanctions(userId);
      }
    } catch (err) {
      console.error('Échec de la modification de la sanction :', err);
      setError('Impossible de modifier la sanction.');
    }
  };

  const handleDelete = async (targetUserId: string, sanctionId: string) => {
    if (!window.confirm('Supprimer définitivement cette sanction ?')) return;
    try {
      await axios.delete(`/api/guilds/${guildId}/sanctions/users/${targetUserId}/${sanctionId}`);
      await fetchUserSanctions(targetUserId);
    } catch {
      setError('La suppression de la sanction a échoué.');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchUserSanctions(userId);
  };

  return (
    <Container fluid className="sanctions-page">
      <Row>
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Rechercher les sanctions d'un utilisateur</h4>
            </Card.Header>
            <Card.Body>
              {reauthRequired && (
                <div className="alert alert-warning" role="alert">
                  <h5>Aucun serveur Discord autorisé n'a été chargé</h5>
                  <p className="mb-3">
                    Déconnectez-vous puis reconnectez-vous avec Discord afin d'actualiser vos serveurs et permissions.
                  </p>
                  <a className="btn btn-primary" href={authUrl}>
                    Actualiser mes permissions Discord
                  </a>
                </div>
              )}
              <Form onSubmit={handleSubmit} className="mb-4">
                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Serveur Discord</Form.Label>
                      <Form.Select value={guildId} onChange={(event) => {
                        setGuildId(event.target.value);
                        setSanctions([]);
                        setError(null);
                      }} required>
                        <option value="">Sélectionner un serveur</option>
                        {guilds.map(guild => (
                          <option key={guild.id} value={guild.id}>
                            {guild.name}{guild.isAdmin ? ' — Administrateur' : ' — Modérateur'}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={10} lg={8} xl={6}>
                    <Form.Group>
                      <Form.Label>Identifiant de l'utilisateur</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <BsPerson />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]{17,20}"
                          placeholder="Saisissez l'identifiant Discord de l'utilisateur"
                          value={userId}
                          onChange={(e) => setUserId(e.target.value)}
                          className="shadow-sm"
                        />
                        <Button 
                          variant="primary" 
                          type="submit"
                          className="d-flex align-items-center"
                        >
                          <BsSearch className="me-2" />
                          Rechercher
                        </Button>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>

              {loading && (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement…</span>
                  </div>
                </div>
              )}

              {error && (
                <div className="alert alert-danger shadow-sm">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}

              {sanctions.length > 0 && (
                <div className="table-responsive shadow-sm rounded">
                  <Table
                    sanctions={sanctions}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    canDelete={Boolean(guilds.find(guild => guild.id === guildId)?.isAdmin)}
                  />
                </div>
              )}

              {!loading && !error && sanctions.length === 0 && userId && (
                <div className="alert alert-info shadow-sm">
                  <i className="bi bi-info-circle me-2"></i>
                  Aucune sanction trouvée pour cet utilisateur.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Sanctions;
